import { Router } from "@angular/router"
import { Component, OnInit, OnDestroy, inject } from "@angular/core"
import { CommonModule } from "@angular/common"
import { RouterOutlet } from "@angular/router"
import {
	MsalService,
	MsalModule,
	MsalBroadcastService
	// MSAL_GUARD_CONFIG,
	// MsalGuardConfiguration
} from "@azure/msal-angular"
import { InteractionStatus, EventMessage, EventType, AccountInfo } from "@azure/msal-browser"
import { Subject } from "rxjs"
import { filter, takeUntil } from "rxjs/operators"
import { EnvService } from "./services/env.service"
import { LoggerService } from "./services/logger.service"
import { AppAuthService } from "./services/app-auth.service"

@Component({
	selector: "app-root",
	templateUrl: "./app.component.html",
	styleUrls: ["./app.component.scss"],
	imports: [CommonModule, MsalModule, RouterOutlet]
})
export class AppComponent implements OnInit, OnDestroy {
	private router = inject(Router)
	private authService = inject(MsalService)
	private msalBroadcastService = inject(MsalBroadcastService)
	private envService = inject(EnvService)
	private loggerService = inject(LoggerService)
	private appAuthService = inject(AppAuthService)

	// private msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG)

	isIframe = false
	private readonly _destroying$ = new Subject<void>()

	constructor() {
		this.envService.loadConfigSync()
	}

	ngOnInit(): void {
		this.authService.instance.addEventCallback((event: EventMessage) => {
			this.loggerService.log("msal-events", "MSAL INTERCEPTOR", event)
		})

		this.router.events.subscribe(event => {
			this.loggerService.log("msal-routes", "this.router.events.subscribe", event)
		})

		this.authService.handleRedirectObservable().subscribe()

		this.isIframe = window !== window.parent && !window.opener // Remove this line to use Angular Universal

		this.authService.instance.enableAccountStorageEvents() // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window

		this.msalBroadcastService.msalSubject$
			.pipe(
				filter(
					(msg: EventMessage) =>
						msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED
				)
			)
			.subscribe((result: EventMessage) => {
				this.loggerService.log(
					"auth",
					"this.msalBroadcastService.msalSubject",
					result,
					this.authService.instance.getAllAccounts()
				)

				if (this.authService.instance.getAllAccounts().length === 0) {
					window.location.href = this.envService.appConfig.msalConfig.auth.postLogoutRedirectUri
				} else {
					window.location.href = this.envService.appConfig.msalConfig.auth.redirectUri
				}
			})

		this.msalBroadcastService.inProgress$
			.pipe(
				filter((status: InteractionStatus) => status === InteractionStatus.None),
				takeUntil(this._destroying$)
			)
			.subscribe(() => {
				this.loggerService.log("auth", "this.msalBroadcastService.inProgress")

				this.checkAndSetActiveAccount()
			})
	}

	checkAndSetActiveAccount() {
		/**
		 * If no active account set but there are accounts signed in, sets first account to active account
		 * To use active account set here, subscribe to inProgress$ first in your component
		 * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
		 */
		const activeAccount = this.authService.instance.getActiveAccount()

		this.loggerService.log(
			"auth",
			"checkAndSetActiveAccount",
			activeAccount,
			this.authService.instance.getAllAccounts()
		)

		if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
			const accounts = this.authService.instance.getAllAccounts()
			this.authService.instance.setActiveAccount(accounts[0])
			this.checkUserGroup(accounts[0])

			window.location.href = this.envService.appConfig.msalConfig.auth.redirectUri
		}
	}

	ngOnDestroy(): void {
		this._destroying$.next(undefined)
		this._destroying$.complete()
	}

	checkUserGroup(account: AccountInfo) {
		const groups = account.idTokenClaims?.["groups"] ? (account.idTokenClaims?.["groups"] as string[]) : []
		const allowedGroups = this.envService.appConfig.msalConfig.allowedGroups
		const hasAllowedGroup = !allowedGroups?.length || allowedGroups.some(group => groups.includes(group))

		if (!groups.length || !hasAllowedGroup) {
			this.appAuthService.logout()
		}
	}
}
